import type { Variants } from "motion/react";
import { useAnimation } from "motion/react";
import * as m from "motion/react-m";
import { forwardRef, useImperativeHandle } from "react";

import { cn } from "@/shared/lib";
import type { AnimatedIconProps } from "@/shared/ui/animated-icon";

export interface RadioTowerIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

const VARIANTS: Variants = {
  normal: {
    opacity: 1,
    transition: {
      duration: 0.4,
    },
  },
  fadeOut: {
    opacity: 0,
    transition: { duration: 0.3 },
  },
  fadeIn: (i: number) => ({
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
      delay: i * 0.1,
    },
  }),
};

const RadioTowerIcon = forwardRef<RadioTowerIconHandle, AnimatedIconProps>(
  ({ className, size = 24, ...props }, ref) => {
    const controls = useAnimation();
    useImperativeHandle(ref, () => {
      return {
        startAnimation: async () => {
          await controls.start("fadeOut");
          controls.start("fadeIn");
        },
        stopAnimation: () => controls.start("normal"),
      };
    });

    return (
      <svg
        className={cn("lucide", className)}
        {...props}
        fill="none"
        height={size}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        width={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <m.path
          animate={controls}
          custom={1}
          d="M4.9 16.1C1 12.2 1 5.8 4.9 1.9"
          initial={{ opacity: 1 }}
          variants={VARIANTS}
        />
        <m.path
          animate={controls}
          custom={0}
          d="M7.8 4.7a6.14 6.14 0 0 0-.8 7.5"
          initial={{ opacity: 1 }}
          variants={VARIANTS}
        />
        <circle cx="12" cy="9" r="2" />
        <m.path
          animate={controls}
          custom={0}
          d="M16.2 4.8c2 2 2.26 5.11.8 7.47"
          initial={{ opacity: 1 }}
          variants={VARIANTS}
        />
        <m.path
          animate={controls}
          custom={1}
          d="M19.1 1.9a9.96 9.96 0 0 1 0 14.1"
          initial={{ opacity: 1 }}
          variants={VARIANTS}
        />
        <path d="M9.5 18h5" />
        <path d="m8 22 4-11 4 11" />
      </svg>
    );
  }
);

RadioTowerIcon.displayName = "RadioTowerIcon";

export { RadioTowerIcon };
